import * as React from "react";
import { Box, Divider, IconButton, Paper, Stack, Typography } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
} from "recharts";
import dayjs from "dayjs";
import { formatConsumptionValue } from '@/utils/consumptionFormat'
import { isolateNumber } from '@/utils/numberDirection'

type Point = { date: Date | string; day: number; night: number };

type WeeklyConsumptionChartProps = {
  data: Point[];
  dateFormatter?: (d: Date) => string;
  title?: string;
  height?: number;
  seriesNames?: { day: string; night: string };
  valueFormatter?: (v: number) => string;
  unit?: string;
  barWidth?: number;
  loading?: boolean;
  /** The read failed: say so, instead of "no data". */
  error?: GatewayError | null;
  onRetry?: () => void;
  navigation?: {
    label: string;
    onPrevious: () => void;
    onNext: () => void;
    previousDisabled?: boolean;
    nextDisabled?: boolean;
  };
};

const GREEN = "#B3E083";
const BLUE = "#316BA5";
const RUBIK = '"Rubik", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';

// Legend text styling - kept for future chart legend implementation
void RUBIK;

function defaultFormat(d: Date) {
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
}
function toDate(v: Date | string): Date {
  if (v instanceof Date) return v;
  // Try dayjs first for better parsing of various date formats
  const parsed = dayjs(v);
  if (parsed.isValid()) return parsed.toDate();
  // Fallback to native Date
  return new Date(v);
}

type CellGeom = { x: number; y: number; width: number; height: number };
type GeomMap = {
  day: Record<number, CellGeom>;
  night: Record<number, CellGeom>;
};

function MeasureCell({
  kind,
  index,
  x,
  y,
  width,
  height,
  geomRef,
}: {
  kind: "day" | "night";
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
  geomRef: React.MutableRefObject<GeomMap>;
}) {
  React.useEffect(() => {
    geomRef.current[kind][index] = { x, y, width, height };
  }, [kind, index, x, y, width, height, geomRef]);

  return null;
}

function PillOverlay({
  left,
  top,
  text,
  bgcolor,
  color,
}: {
  left: number;
  top: number;
  text: string;
  bgcolor: string;
  color: string;
}) {
  return (
    <Paper
      elevation={3}
      sx={{
        position: "absolute",
        left,
        top,
        transform: "translate(-50%, -100%)",
        px: 1.25,
        py: 0.5,
        bgcolor: bgcolor,
        color: color,
        borderRadius: "2px",
        fontFamily: RUBIK,
        fontSize: "12px",
        fontWeight: 300,
        lineHeight: 1,
        whiteSpace: "nowrap",
        "&:after": {
          content: '""',
          position: "absolute",
          left: "50%",
          top: "100%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "6px solid transparent",
          borderRight: "6px solid transparent",
          borderTop: `6px solid ${bgcolor}`,
        },
      }}
    >
      {isolateNumber(text)}
    </Paper>
  );
}

import { useAppTheme } from '@/theme/ThemeContext'
import ChartLoading from '@/components/charts/ChartLoading'
import PageStateMessage from '@/components/common/PageStateMessage'
import { describeGatewayError, type GatewayError } from '@/utils/gatewayError'

export default function WeeklyConsumptionChart({
  data,
  dateFormatter = defaultFormat,
  title = "גרף צריכה יומית בפריסה שבועית",
  height = 320,
  seriesNames = { day: "צ. יום", night: "צ. לילה" },
  valueFormatter,
  unit = " ק׳",
  barWidth = 35,
  loading,
  error,
  onRetry,
  navigation,
}: WeeklyConsumptionChartProps) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const BORDER = isDarkMode ? '#374151' : '#E4E7EB'
  const AXIS_X = isDarkMode ? '#FFFFFF' : '#003F61'
  const TITLE = isDarkMode ? '#FFFFFF' : '#003F61'
  const BG_COLOR = isDarkMode ? '#202B40' : '#fff'
  const TEXT_COLOR = isDarkMode ? '#FFFFFF' : '#000000DE'
  const PILL_BG = isDarkMode ? '#374151FA' : '#003F61CC'
  const PILL_TEXT = '#EFF2F7'

  const [internalOffset, setInternalOffset] = React.useState(0);

  const effectiveNavigation = React.useMemo(() => {
    if (navigation) return navigation;
    if (!data || data.length <= 7) return undefined;
    const total = data.length;
    const maxOffset = Math.max(0, Math.ceil(total / 7) - 1);
    const endIdx = total - internalOffset * 7;
    const startIdx = Math.max(0, endIdx - 7);
    const slice = data.slice(startIdx, endIdx);
    if (!slice.length) return undefined;
    const fStr = dateFormatter(toDate(slice[0].date));
    const lStr = dateFormatter(toDate(slice[slice.length - 1].date));
    return {
      label: `${fStr} - ${lStr}`,
      onPrevious: () => setInternalOffset((prev) => Math.min(prev + 1, maxOffset)),
      onNext: () => setInternalOffset((prev) => Math.max(0, prev - 1)),
      previousDisabled: internalOffset >= maxOffset,
      nextDisabled: internalOffset <= 0,
    };
  }, [navigation, data, internalOffset, dateFormatter]);

  const displayedData = React.useMemo(() => {
    const rawData = data ?? [];
    if (navigation || rawData.length <= 7) return rawData;
    const total = rawData.length;
    const endIdx = total - internalOffset * 7;
    const startIdx = Math.max(0, endIdx - 7);
    return rawData.slice(startIdx, endIdx);
  }, [data, navigation, internalOffset]);

  const legendTextSx = {
    fontFamily: RUBIK,
    fontWeight: 400,
    fontSize: "13px",
    lineHeight: "18px",
    letterSpacing: 0,
    textAlign: "right",
    color: TEXT_COLOR,
    whiteSpace: "nowrap",
  } as const;

  const chartData = React.useMemo(
    () =>
      displayedData.map((pt) => ({
        ...pt,
        _label: String(dateFormatter(toDate(pt.date))).replace(/\./g, '/'),
      })),
    [displayedData, dateFormatter]
  );

  const geomRef = React.useRef<GeomMap>({ day: {}, night: {} });

  const [hover, setHover] = React.useState<
    { index: number; key: "day" | "night" } | null
  >(null);

  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const defaultValueFormatter = React.useCallback(
    (v: number | string) => {
      const n = typeof v === "number" ? v : Number(v);
      if (Number.isFinite(n)) {
        return `${formatConsumptionValue(n)}${unit}`;
      }
      return String(v);
    },
    [unit]
  );

  const formatter =
    valueFormatter
      ? (v: number | string) => valueFormatter(Number(v))
      : defaultValueFormatter;

  const pillPos = React.useMemo(() => {
    if (!hover) return null;
    const g = geomRef.current[hover.key]?.[hover.index];
    const container = containerRef.current;
    const svg = container?.querySelector<SVGSVGElement>("svg");
    if (!g || !container || !svg) return null;

    const containerRect = container.getBoundingClientRect();
    const svgRect = svg.getBoundingClientRect();
    const scaleX = svg.clientWidth ? svgRect.width / svg.clientWidth : 1;
    const scaleY = svg.clientHeight ? svgRect.height / svg.clientHeight : 1;

    return {
      x: svgRect.left - containerRect.left + (g.x + g.width / 2) * scaleX,
      y: svgRect.top - containerRect.top + g.y * scaleY - 6,
    };
  }, [hover]);

  const pillText = React.useMemo(() => {
    if (!hover) return null;
    const row = chartData[hover.index];
    if (!row) return null;
    const val =
      hover.key === "day" ? (row as any).day : (row as any).night;
    return formatter(val);
  }, [hover, chartData, formatter]);

  return (
    <Box
      sx={{
        direction: "rtl",
        borderRadius: 1,
        border: `1px solid ${BORDER}`,
        p: 2,
        bgcolor: BG_COLOR,
        width: '100%',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        mb={1.5}
        sx={{ width: '100%', flexWrap: 'nowrap' }}
      >
        <Stack sx={{ minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{
              fontFamily: RUBIK,
              fontWeight: 500,
              fontSize: { xs: '14px', xl: '15px' },
              lineHeight: 1,
              letterSpacing: 0,
              textAlign: "right",
              color: TITLE,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            {title}
          </Typography>
          {effectiveNavigation && (
            <Typography
              data-testid="weekly-chart-range"
              sx={{
                mt: 0.75,
                fontFamily: RUBIK,
                fontSize: '12px',
                color: isDarkMode ? '#B8C4D6' : '#65758B',
                direction: 'ltr',
                textAlign: 'right',
              }}
            >
              {effectiveNavigation.label}
            </Typography>
          )}
        </Stack>
        <Stack
          direction="row"
          gap={1.5}
          alignItems="center"
          sx={{
            flexWrap: 'nowrap',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" gap={0.5} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: GREEN, flexShrink: 0 }} />
            <Typography sx={legendTextSx}>{seriesNames.day}</Typography>
          </Stack>
          <Stack direction="row" gap={0.5} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BLUE, flexShrink: 0 }} />
            <Typography sx={legendTextSx}>{seriesNames.night}</Typography>
          </Stack>
        </Stack>
      </Stack>
      <Divider sx={{ my: 1 }} />

      <Box sx={{ height, position: 'relative' }}>
        {effectiveNavigation && (
          <>
            <IconButton
              aria-label="לשבוע הקודם"
              onClick={effectiveNavigation.onPrevious}
              disabled={effectiveNavigation.previousDisabled || loading}
              disableRipple
              size="small"
              sx={{
                position: 'absolute',
                left: -4,
                top: '45%',
                transform: 'translateY(-50%)',
                zIndex: 5,
                p: 0.5,
                color: isDarkMode ? '#FFFFFF' : '#003F61',
                bgcolor: 'transparent',
                border: 'none',
                '&:hover': {
                  bgcolor: 'transparent',
                  transform: 'translateY(-50%) scale(1.2)',
                },
                '&.Mui-disabled': {
                  opacity: 0.2,
                  color: isDarkMode ? '#FFFFFF' : '#003F61',
                },
                transition: 'transform 0.15s ease, opacity 0.15s ease',
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 24 }} />
            </IconButton>
            <IconButton
              aria-label="לשבוע הבא"
              onClick={effectiveNavigation.onNext}
              disabled={effectiveNavigation.nextDisabled || loading}
              disableRipple
              size="small"
              sx={{
                position: 'absolute',
                right: -4,
                top: '45%',
                transform: 'translateY(-50%)',
                zIndex: 5,
                p: 0.5,
                color: isDarkMode ? '#FFFFFF' : '#003F61',
                bgcolor: 'transparent',
                border: 'none',
                '&:hover': {
                  bgcolor: 'transparent',
                  transform: 'translateY(-50%) scale(1.2)',
                },
                '&.Mui-disabled': {
                  opacity: 0.2,
                  color: isDarkMode ? '#FFFFFF' : '#003F61',
                },
                transition: 'transform 0.15s ease, opacity 0.15s ease',
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </>
        )}

        {loading ? (
          <ChartLoading height={height} contained={false} />
        ) : error ? (
          <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PageStateMessage
              tone="error"
              title="נתוני הגרף לא נטענו"
              detail={describeGatewayError(error, 'נתוני הגרף לא נטענו')}
              onRetry={onRetry}
            />
          </Box>
        ) : (!data || data.length === 0) ? (
          <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontFamily: RUBIK, fontSize: 16, fontWeight: 300, color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
              אין נתונים להצגה
            </Typography>
          </Box>
        ) : (
      <Box sx={{ height: '100%', position: "relative", px: effectiveNavigation ? 3 : 0 }} ref={containerRef}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 24, right: 12, left: 12, bottom: 22 }}
            barCategoryGap="20%"
            barGap={4}
            onMouseLeave={() => setHover(null)}
            barSize={barWidth}
          >
            <YAxis hide domain={[0, "dataMax * 1.1"]} />
            <XAxis
              dataKey="_label"
              axisLine={{ stroke: AXIS_X, strokeWidth: 1.5 }}
              tickLine={false}
              tick={({ x, y, payload, index }) => {
                const offset =
                  payload?.offset && payload.offset > 0
                    ? payload.offset
                    : (containerRef.current?.clientWidth
                        ? (containerRef.current.clientWidth - 48) / (chartData.length * 2)
                        : 24)
                const isLast = index === chartData.length - 1

                return (
                  <g>
                    {/* Column divider tick at the left edge of this day's slot - directly attached to X axis line */}
                    <line
                      x1={x - offset}
                      x2={x - offset}
                      y1={y - 6}
                      y2={y + 3}
                      stroke={AXIS_X}
                      strokeWidth={1.5}
                    />

                    {/* Column divider tick at the right edge of the last day's slot - directly attached to X axis line */}
                    {isLast && (
                      <line
                        x1={x + offset}
                        x2={x + offset}
                        y1={y - 6}
                        y2={y + 3}
                        stroke={AXIS_X}
                        strokeWidth={1.5}
                      />
                    )}

                    {/* Date label cleanly centered in this slot */}
                    <text
                      x={x}
                      y={y + 16}
                      textAnchor="middle"
                      fontFamily={RUBIK}
                      fontSize={13}
                      fontWeight={400}
                      fill={TITLE}
                    >
                      {payload.value}
                    </text>
                  </g>
                )
              }}
            />

            <defs>
              <linearGradient id="dayGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GREEN} />
                <stop offset="100%" stopColor={`${GREEN}33`} />
              </linearGradient>
              <linearGradient id="nightGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={BLUE} />
                <stop offset="100%" stopColor={`${BLUE}33`} />
              </linearGradient>
            </defs>

            {/* Day Bar (Green) on the left of each day slot matching Figma */}
            <Bar
              dataKey="day"
              name={seriesNames.day}
              fill="url(#dayGrad)"
              radius={[3, 3, 0, 0]}
              maxBarSize={100}
              isAnimationActive={false}
              onMouseEnter={(_, index) => setHover({ index, key: "day" })}
              onMouseMove={(_, index) => setHover({ index, key: "day" })}
              onMouseLeave={() => setHover((h) => (h?.key === "day" ? null : h))}
            >
              <LabelList
                dataKey="day"
                content={(props: any) => (
                  <MeasureCell
                    kind="day"
                    index={props.index}
                    x={props.x}
                    y={props.y}
                    width={props.width}
                    height={props.height}
                    geomRef={geomRef}
                  />
                )}
              />
            </Bar>

            {/* Night Bar (Blue) on the right of each day slot matching Figma */}
            <Bar
              dataKey="night"
              name={seriesNames.night}
              fill="url(#nightGrad)"
              radius={[3, 3, 0, 0]}
              maxBarSize={100}
              isAnimationActive={false}
              onMouseEnter={(_, index) => setHover({ index, key: "night" })}
              onMouseMove={(_, index) => setHover({ index, key: "night" })}
              onMouseLeave={() =>
                setHover((h) => (h?.key === "night" ? null : h))
              }
            >
              <LabelList
                dataKey="night"
                content={(props: any) => (
                  <MeasureCell
                    kind="night"
                    index={props.index}
                    x={props.x}
                    y={props.y}
                    width={props.width}
                    height={props.height}
                    geomRef={geomRef}
                  />
                )}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {hover && pillPos && pillText && (
          <PillOverlay
            left={pillPos.x}
            top={pillPos.y}
            text={pillText}
            bgcolor={PILL_BG}
            color={PILL_TEXT}
          />
        )}
      </Box>
      )}
      </Box>
    </Box>
  );
}
